import React, { useCallback } from 'react';

import { FormikProps } from 'formik';

// components
import CompoundTriggerInput from '../../components/formulas/settings/CompoundTrigger/CompoundTriggerInput';
import TagsInput from '../../components/formulas/settings/FormulaTags/TagsInput';
import { SearchableSelectType } from 'components/inputs/searchableSelect';
import styled from 'styled-components';

// constants
import aampeTheme from '../../constants/aampeTheme';

// hooks
import useAudiencesDetailQuery from '../../hooks/audiences/useAudiencesDetailQuery';
import useRewardFunctions from 'hooks/rewardFunctions/useRewardFunctions';

// models
import { FormulaViewSetupFormValue } from '../../models/formula.model';

import TopicListDropdown from 'views/monitoring/topicManagement/TopicListDropdown';
import RewardFunctionSelect from 'views/rewardFunctions/RewardFunctionSelect';

import AudienceSelect from '../audiences/AudienceSelect';
import FormulaNameInput from './formulaCreateModal/FormulaNameInput';
import FormulaLabelsSection from './formulaSettings/FormulaLabelsSection';

export interface FormulaViewSetupFormProps {
    formikProps: FormikProps<FormulaViewSetupFormValue>;
    hasWriteAccess: boolean;
}
const FormulaViewSetupForm: React.FC<
    React.PropsWithChildren<FormulaViewSetupFormProps>
> = ({ formikProps, hasWriteAccess }) => {
    const { values } = formikProps;
    const { data: audiences } = useAudiencesDetailQuery();

    const onChange = useCallback(
        (field: string, value: string) => {
            formikProps.setFieldValue(field, value?.trim() || null);
        },
        [formikProps],
    );

    const { data: rewardFunctions } = useRewardFunctions();

    return (
        <Container>
            <TagsTitleContainer>
                <FormulaNameInput
                    onTitleChangeMutation={(value) => {
                        formikProps.setFieldValue('name', value.name);
                        return;
                    }}
                    formulaName={values.name as string}
                />
                <SettingsContainer>
                    <TagsInput
                        formikProps={formikProps as any}
                        label={'Custom tags'}
                        placeholder="Add custom tags to organise your catalogue"
                    />
                </SettingsContainer>
            </TagsTitleContainer>

            <TagsTitleContainer>
                <SettingsContainer>
                    <AudienceSelect
                        audiences={audiences ?? []}
                        selected={formikProps.values.audience_id!}
                        handleChange={(value: string) => {
                            formikProps.setFieldValue('audience_id', value);
                        }}
                        label="Audience"
                        showHoverCard
                    />
                </SettingsContainer>
                <SettingsContainer>
                    <CompoundTriggerInput formikProps={formikProps as any} />
                </SettingsContainer>
            </TagsTitleContainer>
            <TagsTitleContainer>
                <RewardFunctionSelect
                    name="select-reward-function"
                    rewardFunctions={rewardFunctions ?? []}
                    handleChange={(value: string) => {
                        onChange('reward_function_id', value);
                    }}
                    initialValue={formikProps.values.reward_function_id}
                    key={formikProps.values.reward_function_id}
                />

                <TopicListDropdown
                    handleChange={(_, value) => {
                        onChange(
                            'topic_id',
                            (value as SearchableSelectType).value as string,
                        );
                    }}
                    selectedTopicValue={formikProps.values.topic_id || ''}
                    isDisabled={!hasWriteAccess}
                />
            </TagsTitleContainer>
            <FormulaLabelsSection
                formulaLabels={values?.labels || []}
                formikProps={formikProps}
                hasWriteAccess={hasWriteAccess}
            />
        </Container>
    );
};

export default FormulaViewSetupForm;

const Container = styled.div({
    display: 'flex',
    width: '100%',
    alignItems: 'start',
    flexDirection: 'column',
    gap: aampeTheme.spacing(3),
});

const TagsTitleContainer = styled.div({
    display: 'flex',
    width: '100%',
    gap: aampeTheme.spacing(3),
    alignItems: 'center',
});

const SettingsContainer = styled.div({
    flex: 1,
});
